<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import UserResource from '@/resources/user'
import CalendarResource from '@/resources/calendar'
import ReservationCheckoutResource from '@/resources/reservationCheckout'
import { createCountdown } from '@/util/timers'
import ApexClubPitch from '../components/ApexClubPitch.vue'
import ApexClubDetail from '../components/ApexClubDetail.vue'

export default {
    components: {
        ApexClubPitch,
        ApexClubDetail
    },
    computed: {
        ...mapGetters(['userResource', 'isApexClub']),
        calendarId() {
            return this.$route.params.calendarId
        },
        upcomingReservationResources() {
            if (!this.upcomingReservationsPageData) {
                return []
            }

            return this.upcomingReservationsPageData.resources
        },
        seatedUserID() {
            return this.seated?.current?.user
        },
        seatedCanProceed() {
            if (this.seatedCanProceedOverride) {
                return true
            }

            return this.seatedUserID == this.userResource.id
        },
        userHasEarlyEntry() {
            if (this.seated?.next?.user != this.userResource.id) {
                return false
            }

            const earlyEntranceDate = (this.seated.next?.user == this.userResource.id) ? moment(this.seated.next?.time).subtract(20, 'minutes') : null
            return earlyEntranceDate && !this.seated.current && this.isApexClub && (moment() > earlyEntranceDate)
        }
    },
    data() {
        return {
            loading: true,
            calendarResource: null,
            checkoutResource: null,
            upcomingReservationsPageData: null,
            seatedCanProceedOverride: false,
            seated: null,
            intervalIDSeated: null,
            countdown: null,
            statusCodes: {
                locked: {
                    text: 'This simulator is locked. Click "Book Now" to unlock',
                    variant: 'danger'
                },
                inUse: {
                    text: 'Someone else is using this simulator. Click "Book Now" to reserve',
                    variant: 'danger'
                },
                partyDisbanded: {
                    text: 'Party has been disbanded',
                    variant: 'info'
                },
            }
        }
    },
    methods: {
        async load() {
            this.calendarResource = await CalendarResource.detail(this.calendarId, { resolveRelated: true })
            this.upcomingReservationsPageData = await UserResource.getReservationsFuture()
            const session = this.$route.query.session || null

            if (this.$route.query.checkout) {
                this.checkoutResource = await ReservationCheckoutResource.detail(this.$route.query.checkout)
            }

            if (this.$route.query.statusCode) {
                if (this.$route.query.statusCode == 'paymentLinkSuccess') {
                    const newQuery = { ...this.$route.query }
                    delete newQuery.statusCode
                    delete newQuery.checkout
                    const value = (this.checkoutResource?.attributes.checkout_data.total_cents) ? this.checkoutResource.attributes.checkout_data.total_cents / 100 : null
                    this.$analytics.track('checkout_success', { value, session, method: 'payment_link', currency: 'USD' })
                    this.$router.replace({ query: newQuery })
                } else {
                    const message = this.statusCodes[this.$route.query.statusCode]
                    this.addMessage(message.variant || 'danger', message.text || 'An unknown error occured')
                }
            }

            await this.fetchSeated()
            this.intervalIDSeated = setInterval(() => this.fetchSeated(), 1e4)
            this.loading = false
        },
        async fetchSeated() {
            this.seated = await this.calendarResource.seated()
            console.log(this.seated)

            if (this.seated?.next?.time && this.seated?.next?.user == this.userResource.id) {
                const timer = createCountdown(this.seated.next.time)

                const countdown = {
                    hours: null,
                    mins: null,
                    secs: null,
                    stop: timer.stop
                }

                timer.emitter.on('tick', (payload) => {
                    if (!payload) {
                        this.countdown = null
                        timer.stop()
                        return null
                    }

                    countdown.hours = payload.hours
                    countdown.mins = payload.mins
                    countdown.secs = payload.secs
                })

                this.countdown = countdown
                timer.start()
            } else {
                this.countdown = null
            }
        },
        onApexClubRegister() {
            this.$refs.apexClubModal.hide()
        }
    },
    async mounted() {
        await this.load()
    },
    beforeDestroy() {
        if (this.intervalIDSeated) {
            clearInterval(this.intervalIDSeated)
        }

        if (this.countdown) {
            this.countdown.stop()
        }
    }
}
</script>

<template>
    <div>
        <b-container v-if="!loading">
            <b-row class="mt-3">
                <b-col md="8" offset-md="2">
                    <messages />
                    
                    <b-card v-if="seatedCanProceed" class="my-3">
                        <h4 class="text-center">"{{ calendarResource }}" está listo para ti!</h4>
                        <p class="text-center">Por favor, dirígete y siéntate</p>
                        <hr />
                        <p class="text-center text-muted">Configura tu carrera en la pantalla del lanzador o haciendo clic en "¡Vamos!" a continuación.</p>
                        <p class="text-center"><b-button :to="{ name: 'calendarControl', params: { calendarId: $route.params.calendarId }, query: { loginUser: userResource.id } }" variant="secondary">¡Vamos!</b-button></p>
                    </b-card>

                    <b-card v-else-if="countdown">
                        <p class="text-center">Tu próxima sesión comienza en</p>
                        <div class="hms text-center">
                            {{ countdown.hours }}:{{ countdown.mins }}:{{ countdown.secs }}
                        </div>
                        <div v-if="isApexClub && seated.next && seated.next.user == userResource.id">
                            <div class="text-center mb-2">
                                <b-badge variant="success"><b-icon icon="check" /> {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</b-badge>
                            </div>
                            <p class="text-center text-muted">¡Estás dentro! ¡Tienes acceso anticipado!</p>
                            <b-button @click="seatedCanProceedOverride = true" :disabled="!userHasEarlyEntry" class="col-sm-12">
                                ¡Déjame entrar temprano!
                            </b-button>
                        </div>
                        <div v-else class="text-center">
                            <hr />
                            <p><strong>¡Los miembros de {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} pueden desbloquear simuladores temprano!</strong> Los miembros de {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} también ahorran un 20% en todas sus carreras y obtienen acceso a carreras exclusivas.</p>
                            <b-button @click="$refs.apexClubModal.show()">Únete al Apex Club</b-button>
                        </div>
                    </b-card>

                    <div v-else>
                        <p class="text-muted text-center">Vuelve aquí dentro de una hora de tu próxima reserva en este simulador ("{{ calendarResource }}").</p>
                        <b-card header="Próximas Reservas">
                            <b-table-simple v-if="upcomingReservationResources.length > 0">
                                <b-tr>
                                    <b-th>Fecha</b-th>
                                    <b-th>Simulador</b-th>
                                    <b-th>&nbsp;</b-th>
                                </b-tr>

                                <b-tr v-for="(reserv, i) in upcomingReservationResources" :key="i">
                                    <b-td>{{ [reserv.getDateFormatted(), reserv.getTimeFormatted()].join(' @ ') }}</b-td>
                                    <b-td>{{ reserv.attributes.calendar_name }}</b-td>
                                    <b-td><a :href="reserv.getAddToCalendarLink()"><b-icon icon="calendar-plus" /> Añadir al Calendario</a></b-td>
                                </b-tr>
                            </b-table-simple>

                            <p class="text-center text-muted" v-else>
                                No hay reservas próximas <br />
                                <b-button variant="secondary" :to="{ name: 'reservationsSingle', params: { calendarId } }" class="mt-3">¡Reserva Ahora!</b-button>
                            </p>
                        </b-card>
                    </div>

                    <b-card class="mt-3 text-center">
                        <p>Ponte listo para la carrera a tu manera. <strong>Personaliza tu coche, pista, asistencias y configuraciones de sesión</strong> antes de salir a la pista. Ya sea que quieras un paseo casual o un desafío competitivo, ajusta tu experiencia en cualquier momento antes de que comience la carrera.</p>
                        <b-button variant="primary" :to="{ name: 'racesPresets' }"><b-icon icon="collection-play" /> Personaliza tu Carrera</b-button>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>

        <b-modal ref="apexClubModal" :title="$appConfig('APEX_CLUB_NAME', 'Apex Club') + ' Inscripción'" size="md" hide-footer no-fade centered>
            <apex-club-pitch @success="onApexClubRegister">
                <p class="text-muted"><small>(Sí, puedes usar tus sesiones para amigos y familiares!) — <a href="javascript:{}" v-b-toggle.collapse-faq>Más Información &amp; FAQ</a></small></p>
                <b-collapse id="collapse-faq" class="mb-3">
                    <b-card>
                        <apex-club-detail />
                    </b-card>
                </b-collapse>
            </apex-club-pitch>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>
.hms {
    font-size: 60px;
}
</style>
